// components/ValidatedAceEditor.tsx
import "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/webpack-resolver";
import React, { useRef } from "react";
import AceEditor from "react-ace";

interface ValidatedAceEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  readOnly?: boolean;
  aceRef: React.RefObject<AceEditor | null>;
  aceErrorsRef: React.RefObject<{ row: number; column: number; type: string; text: string }[]>;
  /*error?: string;*/
}

export const CustomAceEditor: React.FC<ValidatedAceEditorProps> = ({
  value,
  onChange,
  onBlur,
  readOnly = false,
  /*error,*/
  aceRef,
  aceErrorsRef,
}) => {
  const validateAceJson = (content: string) => {
    const editor = aceRef.current?.editor;
    if (editor) {
      const session = editor.getSession();

      const originalSetAnnotations = session.setAnnotations.bind(session);

      session.setAnnotations = function (annotations) {
        const userErrors = aceErrorsRef.current || [];

        // Если воркер пытается стереть наши ошибки — блокируем
        if (annotations.length === 0 && userErrors.length > 0) {
          originalSetAnnotations(userErrors);
          return;
        }

        // Иначе ставим все аннотации: и воркера, и наши
        const merged = [...annotations, ...userErrors];
        originalSetAnnotations(merged);
      };
    }
    if (!editor) return;

    const session = editor.getSession();

    if (!content.trim()) {
      aceErrorsRef.current = [];
      session.clearAnnotations();
      editor.renderer.updateFull();
      return;
    }

    try {
      const customErrors: typeof aceErrorsRef.current = [];

      validateMinMaxRecursively(content, customErrors);

      if (customErrors.length > 0) {
        // Есть ошибки → всегда обновляем
        aceErrorsRef.current = customErrors;
        session.setAnnotations(customErrors);
      } else {
        // Нет ошибок → показываем пусто только если JSON полностью валидный
        // и действительно НЕТ ошибок, а не промежуточное состояние редактирования
        aceErrorsRef.current = [];
        session.clearAnnotations();
      }

      editor.renderer.updateFull();
    } catch {
      //aceErrorsRef.current = [];
    }
  };

  const validateMinMaxRecursively = (content: string, errors: any[]) => {
    // === Построение lineStarts → индекс → (row, col) ===
    const lineStarts: number[] = [0];
    for (let i = 0; i < content.length; i++) {
      if (content[i] === "\n") lineStarts.push(i + 1);
    }

    const indexToRowCol = (idx: number) => {
      let lo = 0,
        hi = lineStarts.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (lineStarts[mid] <= idx) lo = mid + 1;
        else hi = mid - 1;
      }
      const row = Math.max(0, hi);
      const col = idx - lineStarts[row];
      return { row, column: col };
    };

    // === Типы ===
    interface MinMaxRecord {
      value: number;
      idx: number;
      row: number;
      column: number;
    }

    interface Frame {
      fields: Record<
        string, // pattern (унифицированное имя, где min/max заменены на __MM__)
        { min?: MinMaxRecord; max?: MinMaxRecord }
      >;
    }

    const stack: Frame[] = [];

    const isWhitespace = (ch: string) =>
      ch === " " || ch === "\t" || ch === "\r" || ch === "\n";

    // === Поиск паттерна ===
    // min/max могут быть в начале, середине или конце
    const extractMinMaxPattern = (
      key: string
    ): { type: "min" | "max"; pattern: string } | null => {
      const idxMin = key.indexOf("min");
      const idxMax = key.indexOf("max");

      if (idxMin !== -1) {
        const pattern = key.slice(0, idxMin) + "__MM__" + key.slice(idxMin + 3);
        return { type: "min", pattern };
      }

      if (idxMax !== -1) {
        const pattern = key.slice(0, idxMax) + "__MM__" + key.slice(idxMax + 3);
        return { type: "max", pattern };
      }

      return null;
    };

    // === Парсер текста===
    const len = content.length;
    let i = 0;

    let inString = false;
    let stringQuote = '"';
    let escape = false;
    let stringStartIndex = 0;

    while (i < len) {
      const ch = content[i];

      // === ВНУТРИ СТРОКИ ===
      if (inString) {
        if (escape) {
          escape = false;
          i++;
          continue;
        }
        if (ch === "\\") {
          escape = true;
          i++;
          continue;
        }
        if (ch === stringQuote) {
          const raw = content.slice(stringStartIndex, i + 1);
          inString = false;
          i++;

          // Проверяем ключ
          let k = i;
          while (k < len && isWhitespace(content[k])) k++;

          if (k < len && content[k] === ":") {
            const key = raw.replace(/^"|"$/g, "");

            const parsed = extractMinMaxPattern(key);
            if (parsed) {
              const { type, pattern } = parsed;

              k++;
              while (k < len && isWhitespace(content[k])) k++;

              // Ищем число
              const numMatch = content.slice(k).match(/^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?/);

              if (numMatch) {
                const numValue = Number(numMatch[0]);
                const valueIdx = k;
                const pos = indexToRowCol(valueIdx);

                const rec: MinMaxRecord = {
                  value: numValue,
                  idx: valueIdx,
                  row: pos.row,
                  column: pos.column,
                };

                const frame = stack[stack.length - 1];
                if (frame) {
                  if (!frame.fields[pattern]) frame.fields[pattern] = {};
                  frame.fields[pattern][type] = rec;
                }
              }
            }
          }

          continue;
        }
        i++;
        continue;
      }

      // === НЕ В СТРОКЕ ===

      // Начало строки
      if (ch === '"' || ch === "'") {
        inString = true;
        stringQuote = ch;
        escape = false;
        stringStartIndex = i;
        i++;
        continue;
      }

      // Объект
      if (ch === "{") {
        stack.push({ fields: {} });
        i++;
        continue;
      }

      // Закрытие объекта → сверить все пары
      if (ch === "}") {
        const frame = stack.pop();
        if (frame) {
          for (const pattern in frame.fields) {
            const pair = frame.fields[pattern];

            if (pair.min && pair.max) {
              if (pair.min.value > pair.max.value) {
                const pos = indexToRowCol(pair.min.idx);
                errors.push({
                  type: "error",
                  text: `${pattern.replace("__MM__", "min")} (${
                    pair.min.value
                  }) must be <= ${pattern.replace("__MM__", "max")} (${pair.max.value})`,
                  row: pos.row,
                  column: pos.column,
                });
              }
            }
          }
        }
        i++;
        continue;
      }

      i++;
    }

    return errors;
  };

  return (
    <AceEditor
      onChange={(val) => {
        onChange(val);
        validateAceJson(val);
      }}
      onBlur={() => {
        onBlur();
        validateAceJson(value);
      }}
      value={value}
      readOnly={readOnly}
      ref={aceRef}
      mode="json"
      theme="dracula"
      width="100%"
      setOptions={{
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        tabSize: 2,
      }}
    />
  );
};
